import React, { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BsArrowLeft, BsBullseye, BsPlusLg } from 'react-icons/bs';
import { Menu } from '../../components/Menu/Menu';
import LoadingWindow from '../../components/LoadingWindow';
import { automaticCloseAlert } from '../../functions/alerts';
import { getValueCookie } from '../../services/cookieService';
import {
    GOAL_STATUS,
    GOAL_TYPE,
    formatDateRange,
    formatMoney,
    getCurrentCompanyId,
    pickCurrentGoal,
    sortGoalsNewestFirst,
} from '../../services/goalsService';
import GoalFormDialog from './GoalFormDialog';
import {
    GoalMeter,
    GoalStatusBadge,
    GoalsDemoNotice,
    GoalsDenied,
    GoalsTable,
    useGoalDelete,
    useGoalsData,
    useGoalsWithProgress,
    useTargetNames,
} from './GoalParts';
import styles from '../../css/Goals.module.css';

/**
 * Dashboard individual: historial de metas de un vendedor o un customer (TypeUser '1').
 * Rutas: /goals/salesperson/:targetNo y /goals/customer/:targetNo.
 */

function CurrentGoalCard({ goal }) {
    return (
        <section className={styles.panel} aria-labelledby="goal-current-title">
            <div className={styles.sectionHead}>
                <h2 id="goal-current-title" className={styles.panelTitle}>Current goal</h2>
                <GoalStatusBadge status={goal.status} />
            </div>
            <p className={styles.currentDates}>{formatDateRange(goal.startDate, goal.endDate)}</p>
            <GoalMeter goal={goal} large />
            <dl className={styles.currentFacts}>
                <div><dt>Expected</dt><dd>{formatMoney(goal.targetAmount)}</dd></div>
                <div><dt>Sold</dt><dd>{goal.status === GOAL_STATUS.upcoming || goal.achieved === null ? '—' : formatMoney(goal.achieved)}</dd></div>
                <div><dt>Remaining</dt><dd>{goal.remaining === null ? '—' : formatMoney(goal.remaining)}</dd></div>
                <div><dt>{goal.status === GOAL_STATUS.upcoming ? 'Length' : 'Time left'}</dt><dd>{goal.daysLeft} {goal.daysLeft === 1 ? 'day' : 'days'}</dd></div>
            </dl>
        </section>
    );
}

function GoalDetailScreen({ goalType, targetNo }) {
    const idCompany = useMemo(getCurrentCompanyId, []);
    const loadingRef = useRef(null);
    const { data, loadError, reload } = useGoalsData(idCompany, loadingRef);
    const [formOpen, setFormOpen] = useState(false);

    const allGoals = useGoalsWithProgress(data);
    const targetName = useTargetNames(data);
    const onDelete = useGoalDelete(idCompany, data, targetName, reload);

    const goals = useMemo(() => sortGoalsNewestFirst(
        allGoals.filter((g) => g.goalType === goalType && g.targetNo === targetNo),
    ), [allGoals, goalType, targetNo]);

    const current = data ? pickCurrentGoal(goals, data.today) : null;
    const customer = data && goalType === GOAL_TYPE.customer ? data.customers.find((c) => c.customerNo === targetNo) : null;
    const typeLabel = goalType === GOAL_TYPE.customer ? 'Customer' : 'Salesperson';

    const subtitleParts = [typeLabel, targetNo];
    if (customer && customer.city) subtitleParts.push(`${customer.city}, ${customer.state}`);
    if (customer && customer.salespersonNo) subtitleParts.push(`Salesperson ${customer.salespersonNo}`);

    const countByStatus = (status) => goals.filter((g) => g.status === status).length;

    const onSaved = async () => {
        setFormOpen(false);
        await reload();
        automaticCloseAlert('correct', 'Goal created');
    };

    const renderKpi = (label, value) => (
        <div className={styles.kpi}>
            <span className={styles.kpiLabel}>{label}</span>
            <span className={styles.kpiValue}>{value}</span>
        </div>
    );

    return (
        <div className={styles.page}>
            <Menu />
            <LoadingWindow ref={loadingRef} />

            <div className={styles.hero}>
                <div className={styles.heroLeft}>
                    <div className={styles.heroIconBox}><BsBullseye className={styles.heroIcon} aria-hidden="true" /></div>
                    <div className={styles.heroText}>
                        <h1 className={styles.heroTitle}>{data ? targetName(goalType, targetNo) : 'Goals'}</h1>
                        <p className={styles.heroSubtitle}>{subtitleParts.join(' · ')}</p>
                    </div>
                </div>
                {data && (
                    <button type="button" className={styles.heroBtn} onClick={() => setFormOpen(true)}>
                        <BsPlusLg aria-hidden="true" /> New goal
                    </button>
                )}
            </div>

            <div className={styles.body}>
                <Link className={styles.backLink} to="/goals"><BsArrowLeft aria-hidden="true" /> All goals</Link>

                {loadError && (
                    <div className={styles.empty} role="alert">
                        <p>{loadError}</p>
                        <button type="button" className={styles.btnPrimary} onClick={reload}>Try again</button>
                    </div>
                )}
                {data && !loadError && (
                    <>
                        <GoalsDemoNotice data={data} />
                        <div className={styles.kpis}>
                            {renderKpi('Total goals', goals.length)}
                            {renderKpi('In progress', countByStatus(GOAL_STATUS.inProgress))}
                            {renderKpi('Achieved', countByStatus(GOAL_STATUS.achieved))}
                            {renderKpi('Missed', countByStatus(GOAL_STATUS.missed))}
                        </div>

                        {current && <CurrentGoalCard goal={current} />}

                        <section className={styles.section} aria-labelledby="goal-history-title">
                            <h2 id="goal-history-title" className={styles.sectionTitle}>Goal history</h2>
                            {goals.length === 0 ? (
                                <div className={styles.empty}>
                                    <p>There are no goals here yet.</p>
                                    <button type="button" className={styles.btnPrimary} onClick={() => setFormOpen(true)}><BsPlusLg aria-hidden="true" /> New goal</button>
                                </div>
                            ) : (
                                <GoalsTable goals={goals} targetName={targetName} showCreated onDelete={onDelete} caption={`Goal history for ${targetName(goalType, targetNo)}`} />
                            )}
                        </section>
                    </>
                )}
            </div>

            {formOpen && (
                <GoalFormDialog
                    idCompany={idCompany}
                    data={data}
                    initial={{ goalType, targetNo }}
                    onClose={() => setFormOpen(false)}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

export default function GoalDetail() {
    const { goalType, targetNo } = useParams();

    if (getValueCookie('TypeUser') !== '1') return <GoalsDenied />;

    if (goalType !== GOAL_TYPE.salesperson && goalType !== GOAL_TYPE.customer) {
        return (
            <div className={styles.page}>
                <Menu />
                <div className={styles.denied}>
                    <p>This page does not exist.</p>
                    <Link className={styles.tableLink} to="/goals">Go to Goals</Link>
                </div>
            </div>
        );
    }
    // La key reinicia la pantalla si se navega de un objetivo a otro.
    return <GoalDetailScreen key={`${goalType}:${targetNo}`} goalType={goalType} targetNo={targetNo} />;
}
